import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Grid,
  Typography,
  Divider,
  Paper,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel
} from '@mui/material';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useTranslation } from 'react-i18next';
import { getIcon } from '../../../utils/IconHandler';
import EmailTemplateService from '../../../services/EmailTemplateService';
import EmailListService from '../../../services/EmailListService';
import dayjs from 'dayjs';

const EditEmailModal = ({ open, onClose, email, onSave, templates = [], lists = [] }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    templateId: '',
    listId: '',
    subject: '',
    htmlContent: '',
    scheduled: false,
    scheduledDate: null
  });

  // Cargar datos del email al abrir el modal
  useEffect(() => {
    if (email && open) {
      setFormData({
        templateId: email.templateId || '',
        listId: email.listId || '',
        subject: email.subject || '',
        htmlContent: email.htmlContent || '',
        scheduled: email.scheduled || false,
        scheduledDate: email.scheduledDate ? dayjs(email.scheduledDate) : null
      });
    }
  }, [email, open]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Cuando se selecciona una plantilla, cargar su contenido
  useEffect(() => {
    if (formData.templateId) {
      const selectedTemplate = templates.find(t => t.id === parseInt(formData.templateId));
      if (selectedTemplate) {
        setFormData(prev => ({
          ...prev,
          subject: selectedTemplate.subject || prev.subject,
          htmlContent: selectedTemplate.htmlContent || prev.htmlContent
        }));
      }
    }
  }, [formData.templateId, templates]);

  const handleSaveClick = async () => {
    if (!formData.templateId || !formData.listId || !formData.subject || !formData.htmlContent) {
      return;
    }

    if (formData.scheduled && !formData.scheduledDate) {
      return;
    }

    setLoading(true);
    try {
      await onSave({
        ...formData,
        scheduledDate: formData.scheduledDate ? formData.scheduledDate.toISOString() : null
      });
      onClose();
    } catch (error) {
      console.error('Error saving email:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {getIcon('edit')}
        {t('edit_email') || 'Editar Correo'}
      </DialogTitle>
      
      <DialogContent>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>{t('template')}</InputLabel>
              <Select
                value={formData.templateId}
                label={t('template')}
                onChange={(e) => handleChange('templateId', e.target.value)}
              >
                {templates.map((template) => (
                  <MenuItem key={template.id} value={template.id}>
                    {template.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>{t('email_list')}</InputLabel>
              <Select
                value={formData.listId}
                label={t('email_list')}
                onChange={(e) => handleChange('listId', e.target.value)}
              >
                {lists.map((list) => (
                  <MenuItem key={list.id} value={list.id}>
                    {list.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('subject')}
              value={formData.subject}
              onChange={(e) => handleChange('subject', e.target.value)}
              placeholder={t('email_subject_placeholder')}
            />
          </Grid>

          <Grid container spacing={3} item xs={12}>
            <Grid item xs={6}>
              <FormControlLabel
                style={{ display: 'flex', alignItems: 'center', height: '56px' }}
                control={
                  <Switch
                    checked={formData.scheduled}
                    onChange={(e) => handleChange('scheduled', e.target.checked)}
                  />
                }
                label={t('schedule_email')}
              />
            </Grid>
            {formData.scheduled && (
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DateTimePicker
                    label={t('scheduled_date_time')}
                    value={formData.scheduledDate}
                    onChange={(date) => handleChange('scheduledDate', date)}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                    minDateTime={dayjs()}
                  />
                </LocalizationProvider>
              </Grid>
            )}
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
                <Tab label={t('edit_content')} />
                <Tab label={t('preview')} />
              </Tabs>
            </Box>

            {activeTab === 0 && (
              <Box>
                <TextField
                  fullWidth
                  multiline
                  rows={12}
                  label={t('email_content')}
                  value={formData.htmlContent}
                  onChange={(e) => handleChange('htmlContent', e.target.value)}
                  placeholder={t('html_content_placeholder')}
                  sx={{
                    '& .MuiInputBase-input': {
                      fontFamily: 'monospace',
                      fontSize: '0.875rem'
                    }
                  }}
                />
              </Box>
            )}

            {activeTab === 1 && (
              <Paper sx={{ p: 2, minHeight: 300, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h6" gutterBottom>
                  {formData.subject || t('email_preview')}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box
                  dangerouslySetInnerHTML={{ __html: formData.htmlContent || t('no_content') }}
                  sx={{
                    '& img': { maxWidth: '100%' },
                    '& a': { color: 'primary.main' }
                  }}
                />
              </Paper>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} variant="outlined" disabled={loading}>
          {t('cancel')}
        </Button>
        <Button
          onClick={handleSaveClick}
          variant="contained"
          disabled={loading || !formData.templateId || !formData.listId || !formData.subject || !formData.htmlContent}
          startIcon={getIcon('save')}
        >
          {t('save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditEmailModal;

